/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import React, { ComponentType, useCallback, useEffect, useRef, useState } from "react";
import { CosmosGitHubGistFragment } from "@cosmos/hygraph-schema";
import { getGistStyleTag } from "./CosmosGitHubGist.style";
import { css, cx } from "@panda/css";

type IframeRef = {
	current: any;
};

export interface CosmosGitHubGistProps extends CosmosGitHubGistFragment {
	LoadingComponent?: ComponentType;
	className?: string;
}

const generateGistUrl = (username?: string | null, gistId?: string | null, file?: string | null): string => {
	return `https://gist.github.com/${username || ""}/${gistId || ""}.js${file ? `?file=${file}` : ""}`;
};

const getGistId = (username?: string | null, gistId?: string | null, file?: string | null): string => {
	return file ? `gist-${username}-${gistId || ""}-${file || ""}` : `gist-${username}-${gistId || ""}`;
};

const updateContent = (
	iframe: IframeRef,
	handleLoad: () => void,
	handleError: () => void,
	username?: string | null,
	gistId?: string | null,
	file?: string | null,
): void => {
	if (!username || !gistId || !iframe || !iframe.current) {
		return;
	}

	let doc: HTMLDocument = iframe.current.document;
	if (iframe.current.contentDocument) doc = iframe.current.contentDocument;
	else if (iframe.current.contentWindow) doc = iframe.current.contentWindow.document;

	if (!doc) return;

	const gistLink = generateGistUrl(username, gistId, file);
	const gistScript = `<script type="text/javascript" src="${gistLink}"></script>`;
	const elementId = getGistId(username, gistId, file);
	const resizeScript = `onload="parent.document.getElementById('${elementId}').style.height=document.body.scrollHeight + 'px'"`;
	const iframeHtml = `<html><head><base target="_parent">${getGistStyleTag()}</head><body ${resizeScript}>${gistScript}</body></html>`;

	doc?.open();
	doc?.writeln(iframeHtml);
	doc?.close();

	const script = doc?.createElement("script");
	doc?.body?.appendChild(script);
	script.type = "text/javascript";
	script.onload = () => handleLoad();
	script.onerror = () => handleError();
	script.src = gistLink;
};

export const CosmosGitHubGist = ({
	username,
	gistId,
	file,
	LoadingComponent,
	className,
}: CosmosGitHubGistProps): React.JSX.Element | null => {
	const iframeNode: React.MutableRefObject<HTMLIFrameElement | null> = useRef(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const gistElementId = getGistId(username, gistId, file);

	const handleLoad = useCallback((): void => {
		setIsLoading(false);
	}, [setIsLoading]);

	const handleError = useCallback((): void => {
		setIsLoading(false);
		console.error("GitHubt Gist failed to load. Please check the Gist ID or try again.");
	}, [setIsLoading]);

	useEffect(() => {
		updateContent(iframeNode, handleLoad, handleError, username, gistId, file);
	}, [username, file, gistId, handleLoad, handleError]);

	if (!gistId || !username) {
		return null;
	}

	return (
		<div className={cx("github-gist", css({ height: "100%", width: "100%" }), className)}>
			<div id={`${gistElementId}-loading-wrapper`} className={css({ display: isLoading ? "unset" : "none" })}>
				{LoadingComponent ? <LoadingComponent /> : ""}
			</div>
			<iframe
				className={css({ visibility: isLoading ? "hidden" : "visible" })}
				title={`${gistElementId}`}
				onError={handleError}
				ref={iframeNode}
				width="100%"
				height="100%"
				id={gistElementId}
			/>
		</div>
	);
};
